// ValueEdgeBadge — Logic 4 from STRIKZA_FreeSuperTips_Intelligence_Logics.
//
// Surfaces the three numbers behind every signal:
//   model %   — what STRIKZA's intelligence stack thinks the
//                probability is
//   market %  — what the bookmaker line implies
//   edge %    — pp gap (positive only — the badge hides when no edge)
//
// Three tiers (5–9pp / 10–14pp / 15+pp). Hidden entirely below the
// 5pp floor or when the model probability is too low (<40%) — the
// component returns null in those cases so callers can drop it
// directly into a row without conditional wrappers.

import {
  VALUE_EDGE_LABEL,
  bandToBadge,
  computeEdgePct,
  type ValueEdgeTier,
} from "../agents/value-edge.ts";

export interface ValueEdgeBadgeProps {
  readonly modelProb: number | null | undefined;
  readonly marketProb: number | null | undefined;
  /** Compact mode hides the three-numbers row, showing only the
   *  tier chip ("VALUE EDGE +8pp"). For tight leg rows. */
  readonly compact?: boolean;
}

const TIER_COLOR: Record<ValueEdgeTier, { fg: string; border: string; bg: string }> = {
  value: {
    fg: "var(--warn, #f59e0b)",
    border: "var(--warn, #f59e0b)",
    bg: "var(--warn-soft, rgba(245, 158, 11, 0.10))",
  },
  strong: {
    fg: "var(--pos, #22c55e)",
    border: "var(--pos, #22c55e)",
    bg: "var(--pos-soft, rgba(34, 197, 94, 0.10))",
  },
  sharp: {
    fg: "var(--pos, #4ade80)",
    border: "var(--pos, #4ade80)",
    bg: "var(--pos-soft, rgba(74, 222, 128, 0.18))",
  },
};

function fmtPct(prob: number): string {
  return `${(prob * 100).toFixed(0)}%`;
}

export function ValueEdgeBadge({
  modelProb,
  marketProb,
  compact = false,
}: ValueEdgeBadgeProps): JSX.Element | null {
  const edgePct = computeEdgePct(modelProb ?? null, marketProb ?? null);
  const tier = bandToBadge(edgePct, modelProb ?? null);
  if (tier === null || edgePct === null) return null;

  const color = TIER_COLOR[tier];
  const label = VALUE_EDGE_LABEL[tier];

  if (compact) {
    return (
      <span
        className="value-edge-badge value-edge-compact"
        title={`${label} · model ${fmtPct(modelProb ?? 0)} vs market ${fmtPct(marketProb ?? 0)} · +${edgePct}pp`}
        style={{
          fontFamily: "var(--font-mono, monospace)",
          fontSize: 10,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: color.fg,
          background: color.bg,
          padding: "2px 6px",
          border: `1px solid ${color.border}`,
          borderRadius: 4,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        ◆ {label} +{edgePct}pp
      </span>
    );
  }

  return (
    <div
      className={`value-edge-badge value-edge-${tier}`}
      style={{
        background: color.bg,
        border: `1px solid ${color.border}`,
        borderRadius: 6,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono, monospace)",
          fontSize: 10,
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: color.fg,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        ◆ {label}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          fontFamily: "var(--font-mono, monospace)",
          fontSize: 11,
        }}
      >
        <div>
          <div style={{ color: "var(--text-tertiary)", fontSize: 9, letterSpacing: "0.5px" }}>
            STRIKZA model
          </div>
          <div style={{ color: "var(--text-primary)" }}>{fmtPct(modelProb ?? 0)}</div>
        </div>
        <div>
          <div style={{ color: "var(--text-tertiary)", fontSize: 9, letterSpacing: "0.5px" }}>
            Market implies
          </div>
          <div style={{ color: "var(--text-primary)" }}>{fmtPct(marketProb ?? 0)}</div>
        </div>
        <div>
          <div style={{ color: "var(--text-tertiary)", fontSize: 9, letterSpacing: "0.5px" }}>
            Your edge
          </div>
          <div style={{ color: color.fg, fontWeight: 600 }}>+{edgePct}pp</div>
        </div>
      </div>
    </div>
  );
}
